import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import PaperclipIcon from "@redotech/redo-icons/paperclip.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { Flex } from "@redotech/redo-web/flex";
import { cn } from "@redotech/redo-web/tailwind-utils";
import { Option } from "effect";
import type { Value } from "platejs";
import { Plate, PlateContent } from "platejs/react";
import { memo, type ReactNode } from "react";
import type { ReplyMode } from "../../hooks/use-reply-mode";
import type { UseMessageEditorResult } from "./use-message-editor";

export const MessageEditor = memo(function MessageEditor({
  editorState,
  header,
  toolbar,
  placeholder = "Type your message...",
  onChange,
  className,
  attachments,
  onRemoveAttachment,
  actions,
  bottomLeft,
  replyMode,
  disabled = false,
  banner,
}: {
  editorState: UseMessageEditorResult;
  header?: ReactNode;
  toolbar?: ReactNode;
  placeholder?: string;
  onSend?: () => void;
  onChange?: (event: { value: Value }) => void;
  className?: string;
  attachments?: Attachment[];
  onRemoveAttachment?: (index: number) => void;
  onDeleteDraft?: () => void;
  isSendingOrDeleting?: boolean;
  actions?: ReactNode;
  /** Optional content rendered in the bottom-left of the input box (e.g. sources). */
  bottomLeft?: ReactNode;
  replyMode?: ReplyMode;
  /** When true, typing is prevented and styling dims the composer. */
  disabled?: boolean;
  /** Optional notice rendered above the editor (e.g. a block explanation). */
  banner?: ReactNode;
}) {
  const { editor } = editorState;
  const hasAttachments = attachments !== undefined && attachments.length > 0;
  const isInternalNote = replyMode === "internalNote";

  return (
    <div
      className={cn(
        "flex flex-col rounded-md border pb-2 overflow-hidden",
        isInternalNote
          ? "border-amber-300 bg-amber-50 focus-within:border-amber-400 focus-within:ring-amber-200/50 focus-within:ring-[3px]"
          : "border-input bg-background focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        disabled && "opacity-80",
        className,
      )}
    >
      {header}
      {banner && <div className="px-3 pt-3">{banner}</div>}
      <Plate editor={editor} onChange={onChange} readOnly={disabled}>
        {toolbar}
        <PlateContent
          aria-disabled={disabled || undefined}
          className={cn(
            "max-h-[200px] overflow-y-auto outline-none p-3",
            "text-sm placeholder:text-muted-foreground",
            // Reset placeholder styles — Slate inherits bold/italic/underline from the current marks
            "[&_[data-slate-placeholder]]:!font-normal [&_[data-slate-placeholder]]:!not-italic [&_[data-slate-placeholder]]:!no-underline [&_[data-slate-placeholder]]:![text-decoration:none]",
            disabled && "cursor-not-allowed text-muted-foreground",
          )}
          placeholder={disabled ? "" : placeholder}
        />
      </Plate>
      {hasAttachments && (
        <div className="flex flex-wrap gap-2 border-t border-border px-3 py-2">
          {attachments.map((attachment, index) => {
            const description = Option.getOrElse(
              attachment.description,
              () => "Attachment",
            );
            const displayName =
              description || attachment.url.split("/").pop() || "Attachment";

            return (
              <div
                className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs"
                key={attachment.id}
              >
                <PaperclipIcon className="size-3 shrink-0 text-muted-foreground" />
                <span className="max-w-[150px] truncate">{displayName}</span>
                <button
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-background"
                  onClick={() => onRemoveAttachment?.(index)}
                  type="button"
                >
                  <XCloseIcon className="size-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {(actions || bottomLeft) && (
        <Flex
          align="center"
          className="min-w-0 overflow-hidden"
          gap="md"
          justify={bottomLeft ? "space-between" : "flex-end"}
          pt="md"
          px="lg"
        >
          {bottomLeft}
          {actions}
        </Flex>
      )}
    </div>
  );
});
